/**
 * OffboardingCreatePage — Issue #363.
 *
 * 红线 (AI GUIDE §6):
 *   - 离职原因 / 备注属于敏感字段；表单仅短暂保留，提交后由后端脱敏落库。
 *   - 立即离职会立刻冻结对应用户的登录与会话，并把角色绑定置为 revoked。
 *   - 计划离职 effectiveAt 必须晚于当前；后端否则会拒绝。
 */
import { useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  useHrOffboardingMutations,
  type CreateHrOffboardingCaseDto,
} from '@/hooks/useHrOffboarding';

export default function OffboardingCreatePage() {
  const { permissions } = useAuth();
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.OFFBOARDING_WRITE);
  const { createCase, loading, error } = useHrOffboardingMutations();
  const [, navigate] = useLocation();

  const [employeeId, setEmployeeId] = useState('');
  const [userId, setUserId] = useState('');
  const [mode, setMode] = useState<CreateHrOffboardingCaseDto['mode']>('planned');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [reason, setReason] = useState('');

  if (!canWrite) return <AccessDenied resource="新建离职案件" />;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const effective =
      mode === 'immediate'
        ? new Date().toISOString()
        : new Date(effectiveAt).toISOString();
    const body: CreateHrOffboardingCaseDto = {
      employeeId,
      userId: userId || undefined,
      mode,
      effectiveAt: effective,
      reason: reason || undefined,
    };
    const result = await createCase(body);
    if (result?.id) {
      navigate(`/hr/offboarding/${result.id}`);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-xl font-semibold">新建离职案件</h1>
        <p className="text-sm text-slate-500">
          创建后将自动生成资产归还、客户/合同移交、文档转交、任务移交、权限回收
          共 5 项必备交接清单。立即离职会立刻冻结账号；计划离职在
          effectiveAt 后由系统幂等执行。
        </p>
      </header>

      {error ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error.message}
        </Card>
      ) : null}

      <Card className="p-4">
        <form className="grid grid-cols-2 gap-4" onSubmit={onSubmit}>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">员工 ID (必填)</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={employeeId}
              required
              maxLength={50}
              onChange={(e) => setEmployeeId(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">登录账号 ID (可选)</span>
            <input
              type="text"
              className="rounded border border-slate-300 px-2 py-1"
              value={userId}
              maxLength={50}
              onChange={(e) => setUserId(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">模式</span>
            <select
              className="rounded border border-slate-300 px-2 py-1"
              value={mode}
              onChange={(e) =>
                setMode(e.target.value as CreateHrOffboardingCaseDto['mode'])
              }
            >
              <option value="planned">计划离职</option>
              <option value="immediate">立即离职</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-slate-600">
              生效时间 {mode === 'immediate' ? '(立即离职忽略此项)' : '(必填)'}
            </span>
            <input
              type="datetime-local"
              className="rounded border border-slate-300 px-2 py-1"
              value={effectiveAt}
              required={mode === 'planned'}
              disabled={mode === 'immediate'}
              onChange={(e) => setEffectiveAt(e.target.value)}
            />
          </label>
          <label className="col-span-2 flex flex-col gap-1 text-sm">
            <span className="text-slate-600">离职原因 (可选，已脱敏)</span>
            <textarea
              className="rounded border border-slate-300 px-2 py-1"
              value={reason}
              maxLength={500}
              rows={3}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="col-span-2 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-slate-300 px-4 py-1 text-sm"
              onClick={() => navigate('/hr/offboarding')}
            >
              取消
            </button>
            <button
              type="submit"
              className="rounded bg-slate-900 px-4 py-1 text-sm text-white hover:bg-slate-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? '提交中…' : '提交'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
